import { useState } from 'react'
import { useSelector } from 'react-redux'
import useComponentVisible from '../../../utility/hooks/useComponentVisible'
import { getLayoutModel, validationMessage, workspaceSchema, appendAppsToModel } from './constants'
import { v4 as uuid } from 'uuid'
import { useTrackEvent } from '@microsoft/applicationinsights-react-js'
import { appInsights } from '../../../_services/AppInsights'
import { EventType } from '../../../utility/types'
import { useWorkspaceService } from '../../../_services/WorkspaceService'
import { useFeatureGate } from '../../../_hooks/useFeatureGate'
import { IconSquarePlus, IconX } from '@tabler/icons-react'
// @ts-ignore
import { Button, Input } from 'dsm-react-lib'
import CreateWorkspaceModal, {
  TemplateSelection,
  WorkspaceLayout,
  WorkspacePrivacy
} from './modals/CreateWorkspaceModal'
import WorkspaceSwitcherFlyout from './WorkspaceSwitcherFlyout'
import { SelectedAppsWidgets } from './modals/addAppWidgetCatalog'
import { buildCreateWorkspacePayload, TEMPLATE_TYPE } from './modals/createWorkspacePayload'

interface Workspace {
  id: string
  name: string
  icon?: string
  model?: any
  [key: string]: any
}

interface RootState {
  workspaceReducer: {
    workspaces: {
      workspaceList: Workspace[]
    }
  }
}

interface WorkspaceNewTabProps {
  setCurrentApp: (app: { workspaceId: string | null; app: any } | null) => void
  isOpen?: boolean // Optional: control modal visibility from tests/parent
  onToggle?: () => void // Optional: control modal toggle from tests/parent
}

// Exported pure functions for testing
export const validateWorkspaceName = (
  name: string,
  workspaceList: Workspace[]
): { isValid: boolean; message: string } => {
  const wsName = name.trim()
  if (wsName === '') {
    return { isValid: false, message: validationMessage.workspaceName.empty }
  }

  if (!workspaceList || workspaceList.length === 0) {
    return { isValid: true, message: '' }
  }

  const filteredData = workspaceList.filter((item: Workspace) => item.icon !== 'smartflow')
  const isNameExists = filteredData.some(
    (item: Workspace) => item.name.toLowerCase() === wsName.toLowerCase()
  )

  if (isNameExists) {
    return { isValid: false, message: validationMessage.workspaceName.exists }
  }

  return { isValid: true, message: '' }
}

export const buildWorkspaceObject = (name: string) => ({
  ...workspaceSchema,
  name,
  id: uuid(),
  model: getLayoutModel([])
})

const WorkspaceNewTab = ({ setCurrentApp, isOpen, onToggle }: WorkspaceNewTabProps) => {
  const { ref: quickLaunchRef, isComponentVisible, setIsComponentVisible } = useComponentVisible()

  // Full Create-workspace wizard (Centrali 3.0) visibility. Controlled via the
  // `isOpen` prop in tests, otherwise local state driven by the flyout's
  // "Create New" action.
  const [isCreateOpen, setIsCreateOpen] = useState<boolean>(false)
  const showCreateModal = isOpen !== undefined ? isOpen : isCreateOpen
  // The "+" menu visibility: the switcher flyout (3.0) or the legacy quick-launch
  // name menu. Uncontrolled unless a test drives it via `isOpen`.
  const menuOpen = isOpen !== undefined ? isOpen : isComponentVisible

  //@ts-ignore
  const trackWSAdd = useTrackEvent(appInsights, EventType.WorkspaceAdded)
  const { workspaceList } = useSelector((state: RootState) => state.workspaceReducer.workspaces)
  const [workspaceName, setWorkspaceName] = useState<string>('')
  const [isValidName, setIsValidName] = useState<boolean>(true)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const {
    addWorkspaceToList,
    createWorkspace: createWorkspaceApi,
    updateWorkspaceList
  } = useWorkspaceService()
  // The full Create-workspace wizard is a Centrali 3.0 feature; older users keep
  // the lightweight "quick launch" name-only create menu.
  const { value: centrali3_0 } = useFeatureGate('centrali3_0', false)

  const resetForm = () => {
    setWorkspaceName('')
    setIsValidName(true)
    setErrorMessage('')
  }

  // "+" click: toggles the switcher flyout (3.0) or the legacy quick-launch menu.
  const toggleMenu = () => {
    if (onToggle !== undefined) {
      // Controlled mode: just call the provided toggle function
      onToggle()
    } else {
      // Uncontrolled mode: use the hook's setState
      setIsComponentVisible((prevState: boolean) => !prevState)
    }
    resetForm()
  }

  // Close the "+" menu (after creating via the legacy quick-launch).
  const closeMenu = () => {
    if (onToggle !== undefined) {
      onToggle()
    } else {
      setIsComponentVisible(false)
    }
    resetForm()
  }

  // Flyout "Create New" hands off to the full wizard: close the flyout, open modal.
  const openCreateModal = () => {
    setIsComponentVisible(false)
    setIsCreateOpen(true)
    resetForm()
  }

  // Close the full Create-workspace wizard.
  const closeCreateModal = () => {
    if (onToggle !== undefined) {
      onToggle()
    } else {
      setIsCreateOpen(false)
    }
    resetForm()
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value
    setWorkspaceName(newValue)

    const validation = validateWorkspaceName(newValue, workspaceList)
    setIsValidName(validation.isValid)
    setErrorMessage(validation.message)
  }

  const createWorkspace = (
    template: TemplateSelection,
    privacy: WorkspacePrivacy,
    layout?: WorkspaceLayout,
    teamId?: string | null,
    selectedAppsWidgets?: SelectedAppsWidgets
  ) => {
    const validation = validateWorkspaceName(workspaceName, workspaceList)
    if (!validation.isValid) {
      setIsValidName(validation.isValid)
      setErrorMessage(validation.message)
      return
    }

    const workspaceObj: Workspace = buildWorkspaceObject(workspaceName)
    workspaceObj.privacy = privacy
    if (layout) workspaceObj.layout = layout
    if (teamId) workspaceObj.teamId = teamId

    // Clone the chosen template's layout so the new workspace inherits its apps.
    if (template?.type === TEMPLATE_TYPE.WORKSPACE && template.workspace?.model) {
      workspaceObj.model = JSON.parse(JSON.stringify(template.workspace.model))
    } else if (template?.type === TEMPLATE_TYPE.CENTRALI && template.preset?.model) {
      workspaceObj.model = JSON.parse(JSON.stringify(template.preset.model))
    }

    // Add any apps chosen on the "Select apps & widget" step as tabs.
    const selectedApps = selectedAppsWidgets?.apps || []
    if (selectedApps.length) {
      workspaceObj.model = appendAppsToModel(workspaceObj.model, selectedApps)
    }
    if (selectedAppsWidgets?.widgets?.length) {
      workspaceObj.widgets = selectedAppsWidgets.widgets
    }

    setCurrentApp({ workspaceId: null, app: null })
    addWorkspaceToList(workspaceObj)

    // Stamp the server id onto the redux entry once known (mirrors TeamWorkspaces.tsx) so it can later be matched for removal/dedup.
    Promise.resolve(
      createWorkspaceApi(
        buildCreateWorkspacePayload({
          name: workspaceName,
          privacy,
          layout,
          teamId,
          template,
          selectedAppsWidgets
        })
      )
    ).then((resp: any) => {
      const serverId = resp?.id ?? resp?.data?.id
      if (serverId !== null && serverId !== undefined) {
        updateWorkspaceList({ ...workspaceObj, serverWorkspaceId: serverId }, workspaceObj.id)
      }
    })

    trackWSAdd({
      properties: {
        workspaceId: workspaceObj.id,
        workspaceName: workspaceObj.name,
        privacy: workspaceObj.privacy,
        layoutVariant: layout?.variant,
        aliPanel: layout?.aliPanel
      }
    })
    closeCreateModal()
  }

  // Old flow: create a workspace from just a name (no template/privacy wizard).
  const createSimpleWorkspace = () => {
    const validation = validateWorkspaceName(workspaceName, workspaceList)
    if (!validation.isValid) {
      setIsValidName(validation.isValid)
      setErrorMessage(validation.message)
      return
    }
    const workspaceObj: Workspace = buildWorkspaceObject(workspaceName)
    setCurrentApp({ workspaceId: null, app: null })
    addWorkspaceToList(workspaceObj)
    trackWSAdd({
      properties: {
        workspaceId: workspaceObj.id,
        workspaceName: workspaceObj.name
      }
    })
    closeMenu()
  }

  return (
    <>
      <div className="w-9 h-9 p-0 flex-shrink-0 relative ml-[6px]">
        <div
          className={`cursor-pointer p-[5px] flex items-center justify-center transition-all duration-200 ease-in-out ${
            menuOpen || showCreateModal
              ? 'rounded-full bg-gray-200'
              : 'rounded-none bg-transparent hover:rounded-full hover:bg-gray-200'
          }`}
          onClick={toggleMenu}>
          {!centrali3_0 && menuOpen ? (
            <IconX size={20} color="var(--text-heading)" />
          ) : (
            <IconSquarePlus size={20} color="var(--text-subheading)" />
          )}
        </div>
        {/* Centrali 3.0 workspace switcher flyout (mirrors the left-nav submenu). */}
        {centrali3_0 && (
          <WorkspaceSwitcherFlyout
            ref={quickLaunchRef}
            isOpen={isComponentVisible}
            onClose={() => setIsComponentVisible(false)}
            onCreateNew={openCreateModal}
          />
        )}
        {/* Old (pre-3.0) quick-launch create menu. */}
        {!centrali3_0 && menuOpen && (
          <div
            ref={quickLaunchRef}
            data-testid="workspace-quick-launch"
            className="p-[18px] gap-[10px] w-[235px] bg-[var(--surface-bg)] rounded-[2px] z-[2] relative top-[6px] flex-wrap shadow-[0px_0px_3px_var(--text-subheading)] overflow-hidden">
            <div className="pr-50">
              <Input
                label="Workspace name"
                placeholder="e.g ESG workspace"
                invalid={!isValidName}
                helperText={errorMessage}
                onChange={handleChange}
                value={workspaceName}
                maxLength={50}
                autoFocus
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                  e.key === 'Enter' ? createSimpleWorkspace() : null
                }
                onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
              />
            </div>
            <div className="mt-2 d-flex justify-content-between pr-50">
              <Button label="Cancel" color="secondary" variant="outline" onClick={closeMenu} />
              <Button label="Create" disabled={!isValidName} onClick={createSimpleWorkspace} />
            </div>
          </div>
        )}
      </div>
      {/* New (Centrali 3.0) full create-workspace wizard. */}
      {centrali3_0 && (
        <CreateWorkspaceModal
          isOpen={showCreateModal}
          toggle={closeCreateModal}
          workspaceName={workspaceName}
          isValidName={isValidName}
          errorMessage={errorMessage}
          onNameChange={handleChange}
          workspaceList={workspaceList}
          onCreate={createWorkspace}
        />
      )}
    </>
  )
}

export default WorkspaceNewTab
